import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';

type NotificationTemplate = {
  enabled: boolean;
  subject: string;
  html: string;
  text: string;
};

type NotificationEventDef = {
  key: string;
  title: string;
  description: string;
  variables: string[];
  defaults: Omit<NotificationTemplate, 'enabled'> & { enabled?: boolean };
};

type EditField = 'subject' | 'html' | 'text';

const EVENT_DEFS: NotificationEventDef[] = [
  {
    key: 'user_signup',
    title: 'User signed up',
    description: 'Welcome email after a user creates an account.',
    variables: ['user.firstName', 'user.lastName', 'user.email', 'app.name', 'app.url'],
    defaults: {
      enabled: true,
      subject: 'Welcome to {{app.name}}, {{user.firstName}}!',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Welcome, {{user.firstName}}!</h2>\n  <p>Your account is ready. You can sign in anytime.</p>\n  <p><a href="{{app.url}}">Open {{app.name}}</a></p>\n</div>',
      text: 'Welcome, {{user.firstName}}! Your account is ready. Open: {{app.url}}',
    },
  },
  {
    key: 'product_uploaded',
    title: 'Product uploaded',
    description: 'Confirmation email after a user publishes/uploads a product.',
    variables: ['user.firstName', 'user.lastName', 'user.email', 'product.id', 'product.name', 'product.price', 'app.name', 'app.url'],
    defaults: {
      enabled: true,
      subject: 'Your product is live on {{app.name}}: {{product.name}}',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Nice! Your product is live</h2>\n  <p>Hi {{user.firstName}},</p>\n  <p>We successfully published your product:</p>\n  <p><b>{{product.name}}</b><br/>ID: {{product.id}}<br/>Price: {{product.price}}</p>\n  <p><a href="{{app.url}}">Open {{app.name}}</a></p>\n</div>',
      text: 'Your product is live: {{product.name}} ({{product.id}}). Price: {{product.price}}. Open {{app.name}}: {{app.url}}',
    },
  },
  {
    key: 'user_email_verified',
    title: 'Email verified',
    description: 'Confirmation email after a user verifies their email address.',
    variables: ['user.firstName', 'user.email', 'app.name', 'app.url'],
    defaults: {
      enabled: false,
      subject: 'Your email is verified',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>All set!</h2>\n  <p>Your email ({{user.email}}) is now verified.</p>\n  <p><a href="{{app.url}}">Go to {{app.name}}</a></p>\n</div>',
      text: 'Your email ({{user.email}}) is now verified. Go to {{app.url}}',
    },
  },
  {
    key: 'transaction_completed',
    title: 'Transaction completed',
    description: 'Receipt/confirmation email after a successful transaction.',
    variables: [
      'user.firstName',
      'user.email',
      'buyer.name',
      'buyer.email',
      'transaction.id',
      'transaction.total',
      'transaction.currency',
      'transaction.productsCount',
      'transaction.productsSummary',
      'transaction.productsSummaryShort',
      'transaction.sellersSummary',
      'transaction.sellersCount',
      'purchase.itemsCount',
      'purchase.itemsSummary',
      'purchase.itemsSummaryShort',
      'purchase.total',
      'app.name',
      'app.url',
    ],
    defaults: {
      enabled: true,
      subject: 'Payment received • {{transaction.productsSummaryShort}} • {{transaction.total}} {{transaction.currency}}',
      html:
        '<div style="font-family:Arial,sans-serif;line-height:1.5;color:#111827">\n' +
        '  <div style="max-width:640px;margin:0 auto;border:1px solid #e5e7eb;border-radius:16px;overflow:hidden">\n' +
        '    <div style="background:linear-gradient(135deg,#7c3aed 0%,#6d28d9 100%);padding:18px 22px">\n' +
        '      <div style="font-size:14px;color:rgba(255,255,255,0.9);letter-spacing:0.3px">{{app.name}}</div>\n' +
        '      <div style="font-size:22px;font-weight:800;color:white;margin-top:6px">Payment received</div>\n' +
        '    </div>\n' +
        '    <div style="padding:22px;background:#ffffff">\n' +
        '      <p style="margin:0 0 10px 0">Hi {{user.firstName}},</p>\n' +
        '      <p style="margin:0 0 16px 0;color:#374151">Thanks for your purchase! Here is your receipt:</p>\n' +
        '      <div style="margin:0 0 14px 0;padding:12px 14px;border-radius:14px;border:1px solid #e5e7eb;background:#f9fafb">\n' +
        '        <div style="font-size:13px;color:#6b7280">Products</div>\n' +
        '        <div style="font-size:14px;font-weight:800;color:#111827;margin-top:4px">{{transaction.productsSummaryShort}}</div>\n' +
        '        <div style="font-size:12px;color:#6b7280;margin-top:6px">Seller(s): {{transaction.sellersSummary}}</div>\n' +
        '      </div>\n' +
        '      <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:14px;padding:14px 16px">\n' +
        '        <div style="display:flex;justify-content:space-between;gap:10px">\n' +
        '          <div style="font-size:13px;color:#6b7280">Transaction ID</div>\n' +
        '          <div style="font-size:13px;font-weight:700;color:#111827">{{transaction.id}}</div>\n' +
        '        </div>\n' +
        '        <div style="display:flex;justify-content:space-between;gap:10px;margin-top:8px">\n' +
        '          <div style="font-size:13px;color:#6b7280">Total</div>\n' +
        '          <div style="font-size:16px;font-weight:900;color:#111827">{{transaction.total}} {{transaction.currency}}</div>\n' +
        '        </div>\n' +
        '      </div>\n' +
        '      <div style="margin-top:16px">\n' +
        '        <div style="font-size:13px;color:#6b7280;margin-bottom:6px">Items</div>\n' +
        '        <pre style="margin:0;background:#0b1020;color:#e5e7eb;padding:12px 14px;border-radius:12px;font-size:13px;white-space:pre-wrap">{{transaction.productsSummary}}</pre>\n' +
        '      </div>\n' +
        '      <div style="margin-top:18px">\n' +
        '        <a href="{{app.url}}" style="display:inline-block;background:linear-gradient(135deg,#7c3aed 0%,#6d28d9 100%);color:white;text-decoration:none;font-weight:800;padding:12px 16px;border-radius:12px">Open {{app.name}}</a>\n' +
        '      </div>\n' +
        '      <p style="margin:18px 0 0 0;font-size:12px;color:#9ca3af">If you did not make this purchase, please contact support.</p>\n' +
        '    </div>\n' +
        '  </div>\n' +
        '</div>',
      text:
        'Payment received.\n' +
        'Products: {{transaction.productsSummaryShort}}\n' +
        'Seller(s): {{transaction.sellersSummary}}\n' +
        'Transaction ID: {{transaction.id}}\n' +
        'Total: {{transaction.total}} {{transaction.currency}}\n' +
        'Items:\n{{transaction.productsSummary}}\n' +
        'Open {{app.name}}: {{app.url}}',
    },
  },
  {
    key: 'product_sold',
    title: 'Product sold',
    description: 'Seller confirmation email when one of their products is purchased.',
    variables: [
      'user.firstName',
      'user.email',
      'buyer.name',
      'buyer.email',
      'purchase.itemsCount',
      'purchase.itemsSummary',
      'purchase.itemsSummaryShort',
      'purchase.total',
      'purchase.currency',
      'transaction.id',
      'app.name',
      'app.url',
    ],
    defaults: {
      enabled: true,
      subject: 'You sold {{purchase.itemsSummaryShort}} • {{purchase.total}} {{purchase.currency}}',
      html:
        '<div style="font-family:Arial,sans-serif;line-height:1.55;color:#111827">\n' +
        '  <div style="max-width:640px;margin:0 auto;border:1px solid #e5e7eb;border-radius:18px;overflow:hidden">\n' +
        '    <div style="background:linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%);padding:18px 22px">\n' +
        '      <div style="font-size:13px;color:rgba(255,255,255,0.92);letter-spacing:0.35px">{{app.name}} · Seller update</div>\n' +
        '      <div style="font-size:22px;font-weight:900;color:white;margin-top:6px">Product sold</div>\n' +
        '    </div>\n' +
        '    <div style="padding:22px;background:#ffffff">\n' +
        '      <p style="margin:0 0 10px 0">Hi {{user.firstName}},</p>\n' +
        '      <p style="margin:0 0 16px 0;color:#374151">Good news — you just made a sale.</p>\n' +
        '      <div style="margin:0 0 14px 0;padding:14px 16px;border-radius:14px;border:1px solid #e5e7eb;background:#f8fafc">\n' +
        '        <div style="font-size:13px;color:#6b7280">Items sold</div>\n' +
        '        <div style="font-size:15px;font-weight:900;color:#0f172a;margin-top:4px">{{purchase.itemsSummaryShort}}</div>\n' +
        '        <div style="font-size:12px;color:#6b7280;margin-top:8px">Buyer: <b style="color:#111827">{{buyer.name}}</b> ({{buyer.email}})</div>\n' +
        '      </div>\n' +
        '      <div style="display:flex;gap:12px;flex-wrap:wrap">\n' +
        '        <div style="flex:1;min-width:240px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:14px;padding:14px 16px">\n' +
        '          <div style="font-size:12px;color:#6b7280">Your payout (gross)</div>\n' +
        '          <div style="font-size:18px;font-weight:900;color:#111827;margin-top:6px">{{purchase.total}} {{purchase.currency}}</div>\n' +
        '          <div style="font-size:12px;color:#9ca3af;margin-top:4px">(Before fees, if applicable)</div>\n' +
        '        </div>\n' +
        '        <div style="flex:1;min-width:240px;background:#ffffff;border:1px dashed rgba(37,99,235,0.35);border-radius:14px;padding:14px 16px">\n' +
        '          <div style="font-size:12px;color:#6b7280">Transaction ID</div>\n' +
        '          <div style="font-size:13px;font-weight:800;color:#111827;margin-top:6px">{{transaction.id}}</div>\n' +
        '        </div>\n' +
        '      </div>\n' +
        '      <div style="margin-top:16px">\n' +
        '        <div style="font-size:13px;color:#6b7280;margin-bottom:6px">Items</div>\n' +
        '        <pre style="margin:0;background:#0b1020;color:#e5e7eb;padding:12px 14px;border-radius:12px;font-size:13px;white-space:pre-wrap">{{purchase.itemsSummary}}</pre>\n' +
        '      </div>\n' +
        '      <div style="margin-top:18px">\n' +
        '        <a href="{{app.url}}" style="display:inline-block;background:linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%);color:white;text-decoration:none;font-weight:900;padding:12px 16px;border-radius:12px">Open {{app.name}}</a>\n' +
        '      </div>\n' +
        '      <p style="margin:18px 0 0 0;font-size:12px;color:#9ca3af">Tip: Ship quickly and add tracking to improve delivery confidence.</p>\n' +
        '    </div>\n' +
        '  </div>\n' +
        '</div>',
      text:
        'Product sold.\n' +
        'Items: {{purchase.itemsSummaryShort}}\n' +
        'Buyer: {{buyer.name}} ({{buyer.email}})\n' +
        'Total: {{purchase.total}} {{purchase.currency}}\n' +
        'Transaction ID: {{transaction.id}}\n' +
        'Items list:\n{{purchase.itemsSummary}}\n' +
        'Open {{app.name}}: {{app.url}}',
    },
  },
  {
    key: 'delivery_status_changed',
    title: 'Delivery status changed',
    description: 'Buyer notification when the seller updates shipping/delivery progress.',
    variables: [
      'user.firstName',
      'user.email',
      'product.id',
      'product.name',
      'purchase.id',
      'seller.name',
      'delivery.status',
      'delivery.previousStatus',
      'delivery.statusLabel',
      'delivery.trackingNumber',
      'delivery.carrier',
      'delivery.note',
      'app.name',
      'app.url',
      'support.email',
    ],
    defaults: {
      enabled: true,
      subject: 'Shipping update • {{product.name}} • {{delivery.statusLabel}}',
      html:
        '<div style="font-family:Arial,sans-serif;line-height:1.55;color:#111827">\n' +
        '  <div style="max-width:640px;margin:0 auto;border:1px solid #e5e7eb;border-radius:18px;overflow:hidden">\n' +
        '    <div style="background:linear-gradient(135deg,#22c55e 0%,#06b6d4 60%,#2563eb 100%);padding:18px 22px">\n' +
        '      <div style="font-size:13px;color:rgba(255,255,255,0.92);letter-spacing:0.35px">{{app.name}} · Delivery update</div>\n' +
        '      <div style="font-size:22px;font-weight:900;color:white;margin-top:6px">Your order is moving</div>\n' +
        '    </div>\n' +
        '    <div style="padding:22px;background:#ffffff">\n' +
        '      <p style="margin:0 0 10px 0">Hi {{user.firstName}},</p>\n' +
        '      <p style="margin:0 0 16px 0;color:#374151">Good news — your seller updated the delivery status for <b>{{product.name}}</b>.</p>\n' +
        '      <div style="margin:0 0 14px 0;padding:14px 16px;border-radius:14px;border:1px solid #e5e7eb;background:#f8fafc">\n' +
        '        <div style="font-size:13px;color:#6b7280">Current status</div>\n' +
        '        <div style="font-size:16px;font-weight:900;color:#0f172a;margin-top:4px">{{delivery.statusLabel}}</div>\n' +
        '        <div style="font-size:12px;color:#6b7280;margin-top:8px">Seller: <b style="color:#111827">{{seller.name}}</b></div>\n' +
        '      </div>\n' +
        '      <div style="display:flex;gap:12px;flex-wrap:wrap">\n' +
        '        <div style="flex:1;min-width:240px;background:#ffffff;border:1px dashed rgba(37,99,235,0.35);border-radius:14px;padding:14px 16px">\n' +
        '          <div style="font-size:12px;color:#6b7280">Tracking</div>\n' +
        '          <div style="font-size:14px;font-weight:900;color:#111827;margin-top:6px">{{delivery.trackingNumber}}</div>\n' +
        '          <div style="font-size:12px;color:#9ca3af;margin-top:4px">Carrier: {{delivery.carrier}}</div>\n' +
        '        </div>\n' +
        '        <div style="flex:1;min-width:240px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:14px;padding:14px 16px">\n' +
        '          <div style="font-size:12px;color:#6b7280">Order</div>\n' +
        '          <div style="font-size:13px;font-weight:800;color:#111827;margin-top:6px">{{purchase.id}}</div>\n' +
        '        </div>\n' +
        '      </div>\n' +
        '      <div style="margin-top:14px">\n' +
        '        <div style="font-size:13px;color:#6b7280;margin-bottom:6px">Seller message</div>\n' +
        '        <div style="background:#0b1020;color:#e5e7eb;padding:12px 14px;border-radius:12px;font-size:13px">{{delivery.note}}</div>\n' +
        '      </div>\n' +
        '      <div style="margin-top:18px">\n' +
        '        <a href="{{app.url}}" style="display:inline-block;background:linear-gradient(135deg,#22c55e 0%,#06b6d4 60%,#2563eb 100%);color:white;text-decoration:none;font-weight:900;padding:12px 16px;border-radius:12px">Open {{app.name}}</a>\n' +
        '      </div>\n' +
        '      <p style="margin:18px 0 0 0;font-size:12px;color:#9ca3af">Need help? Contact support at {{support.email}}.</p>\n' +
        '    </div>\n' +
        '  </div>\n' +
        '</div>',
      text:
        'Shipping update.\n' +
        'Product: {{product.name}}\n' +
        'Status: {{delivery.statusLabel}}\n' +
        'Tracking: {{delivery.trackingNumber}} ({{delivery.carrier}})\n' +
        'Order: {{purchase.id}}\n' +
        'Seller message: {{delivery.note}}\n' +
        'Open {{app.name}}: {{app.url}}\n' +
        'Support: {{support.email}}',
    },
  },
  {
    key: 'transaction_failed',
    title: 'Transaction failed',
    description: 'Notification when a transaction fails (optional, can be support-focused).',
    variables: ['user.firstName', 'transaction.id', 'app.name', 'support.email'],
    defaults: {
      enabled: false,
      subject: 'Payment failed ({{transaction.id}})',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Payment failed</h2>\n  <p>Hi {{user.firstName}}, your payment could not be completed.</p>\n  <p>If you need help, contact us at {{support.email}}.</p>\n</div>',
      text: 'Payment failed ({{transaction.id}}). Help: {{support.email}}',
    },
  },
  {
    key: 'password_reset_requested',
    title: 'Password reset requested',
    description: 'Email with a reset link/token when the user requests a password reset.',
    variables: ['user.firstName', 'reset.url', 'app.name'],
    defaults: {
      enabled: true,
      subject: 'Reset your {{app.name}} password',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Password reset</h2>\n  <p>Hi {{user.firstName}},</p>\n  <p><a href="{{reset.url}}">Click here to reset your password</a></p>\n</div>',
      text: 'Reset your password: {{reset.url}}',
    },
  },
  {
    key: 'order_created',
    title: 'Order created',
    description: 'Email after an order is created.',
    variables: ['user.firstName', 'order.id', 'order.total', 'order.currency', 'app.name'],
    defaults: {
      enabled: false,
      subject: 'Order confirmed ({{order.id}})',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Order confirmed</h2>\n  <p>Hi {{user.firstName}}, your order {{order.id}} has been created.</p>\n  <p>Total: {{order.total}} {{order.currency}}</p>\n</div>',
      text: 'Order confirmed: {{order.id}} Total: {{order.total}} {{order.currency}}',
    },
  },
  {
    key: 'order_shipped',
    title: 'Order shipped',
    description: 'Email after an order is shipped with tracking info.',
    variables: ['user.firstName', 'order.id', 'shipment.trackingUrl', 'shipment.carrier', 'app.name'],
    defaults: {
      enabled: false,
      subject: 'Your order {{order.id}} is on the way',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Shipped!</h2>\n  <p>Hi {{user.firstName}}, your order {{order.id}} has shipped via {{shipment.carrier}}.</p>\n  <p><a href="{{shipment.trackingUrl}}">Track your package</a></p>\n</div>',
      text: 'Order shipped: {{order.id}} Track: {{shipment.trackingUrl}}',
    },
  },
];

const buildDefaults = (): Record<string, NotificationTemplate> => {
  const out: Record<string, NotificationTemplate> = {};
  EVENT_DEFS.forEach((d) => {
    out[d.key] = {
      enabled: Boolean(d.defaults.enabled),
      subject: d.defaults.subject,
      html: d.defaults.html,
      text: d.defaults.text,
    };
  });
  return out;
};

const PlatformNotificationsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [updatedAt, setUpdatedAt] = React.useState<string | null>(null);

  const [items, setItems] = React.useState<Record<string, NotificationTemplate>>(() => buildDefaults());
  const [activeKey, setActiveKey] = React.useState<string>(EVENT_DEFS[0]?.key || '');
  const [tabByKey, setTabByKey] = React.useState<Record<string, 'html' | 'text' | 'preview'>>({});

  const inputRefs = React.useRef<Record<string, Partial<Record<EditField, HTMLInputElement | HTMLTextAreaElement | null>>>>({});
  const lastFocusRef = React.useRef<{ key: string; field: EditField } | null>(null);

  const callApi = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/platform-notifications.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const msg = json?.error || raw || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }

    return json;
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await callApi('load');
      const defaults = buildDefaults();
      const stored = json?.items && typeof json.items === 'object' ? json.items : {};
      const merged: Record<string, NotificationTemplate> = { ...defaults };

      Object.keys(stored).forEach((k) => {
        const v = stored[k];
        if (!v || typeof v !== 'object') return;
        merged[k] = {
          enabled: Boolean((v as any).enabled),
          subject: String((v as any).subject || ''),
          html: String((v as any).html || ''),
          text: String((v as any).text || ''),
        };
      });

      setItems(merged);
      setUpdatedAt(typeof json?.updatedAt === 'string' ? json.updatedAt : null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  React.useEffect(() => {
    load();
  }, [load]);

  const saveAll = React.useCallback(async (next?: Record<string, NotificationTemplate>) => {
    const payload = next || items;
    try {
      setSaving(true);
      const json = await callApi('save', { items: payload });
      setUpdatedAt(typeof json?.updatedAt === 'string' ? json.updatedAt : null);
      toast.success('Saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [callApi, items]);

  const autosaveTimerRef = React.useRef<number | null>(null);
  const autosave = React.useCallback(
    (next: Record<string, NotificationTemplate>) => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = window.setTimeout(() => {
        callApi('save', { items: next })
          .then((json) => {
            setUpdatedAt(typeof json?.updatedAt === 'string' ? json.updatedAt : null);
          })
          .catch(() => {
            // best-effort
          });
      }, 800);
    },
    [callApi]
  );

  const resetAllToDefaults = React.useCallback(() => {
    const ok = window.confirm('Reset all notification templates to defaults? This will overwrite your current templates.');
    if (!ok) return;
    const defaults = buildDefaults();
    setItems(defaults);
    autosave(defaults);
    toast.success('Reset to defaults');
  }, [autosave]);

  const resetEventToDefaults = React.useCallback(
    (key: string) => {
      const ok = window.confirm('Reset this notification template to its default?');
      if (!ok) return;
      const defaults = buildDefaults();
      setItems((prev) => {
        const next = { ...prev, [key]: defaults[key] };
        autosave(next);
        return next;
      });
      toast.success('Reset to default');
    },
    [autosave]
  );

  React.useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  const updateItem = (key: string, patch: Partial<NotificationTemplate>) => {
    setItems((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || buildDefaults()[key]), ...patch } };
      autosave(next);
      return next;
    });
  };

  const getTab = (key: string): 'html' | 'text' | 'preview' => {
    return tabByKey[key] || 'html';
  };

  const setTab = (key: string, tab: 'html' | 'text' | 'preview') => {
    setTabByKey((p) => ({ ...p, [key]: tab }));
  };

  const setRef = React.useCallback((key: string, field: EditField, el: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (!inputRefs.current[key]) inputRefs.current[key] = {};
    inputRefs.current[key][field] = el;
  }, []);

  const setLastFocus = React.useCallback((key: string, field: EditField) => {
    lastFocusRef.current = { key, field };
  }, []);

  const getEl = React.useCallback((key: string, field: EditField) => {
    return (inputRefs.current[key] && inputRefs.current[key][field]) || null;
  }, []);

  const insertTokenIntoField = React.useCallback(
    (key: string, field: EditField, token: string) => {
      const el = getEl(key, field);
      const tpl = items[key] || buildDefaults()[key];
      const current = String((tpl as any)[field] || '');

      const start = el && typeof (el as any).selectionStart === 'number' ? (el as any).selectionStart : current.length;
      const end = el && typeof (el as any).selectionEnd === 'number' ? (el as any).selectionEnd : current.length;
      const next = current.slice(0, start) + token + current.slice(end);

      updateItem(key, { [field]: next } as any);

      // Restore caret position after state update
      window.setTimeout(() => {
        const el2 = getEl(key, field);
        if (!el2) return;
        try {
          el2.focus();
          const pos = start + token.length;
          (el2 as any).setSelectionRange(pos, pos);
        } catch {
          // ignore
        }
      }, 0);
    },
    [getEl, items, updateItem]
  );

  const insertVar = React.useCallback(
    (key: string, v: string) => {
      const token = `{{${v}}}`;
      const focus = lastFocusRef.current;

      // If user is currently editing this accordion, insert into the focused field
      if (focus && focus.key === key) {
        insertTokenIntoField(key, focus.field, token);
        return;
      }

      // Otherwise, default to active editor tab (HTML/Text)
      const tab = getTab(key);
      insertTokenIntoField(key, tab === 'text' ? 'text' : 'html', token);
    },
    [getTab, insertTokenIntoField]
  );

  const onVarDragStart = (e: React.DragEvent, v: string) => {
    const token = `{{${v}}}`;
    e.dataTransfer.setData('text/plain', token);
    e.dataTransfer.setData('application/x-ml-token', token);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onDropToken = (e: React.DragEvent, key: string, field: EditField) => {
    e.preventDefault();
    const token = e.dataTransfer.getData('application/x-ml-token') || e.dataTransfer.getData('text/plain');
    if (!token) return;

    setLastFocus(key, field);
    // ensure selectionStart reflects drop target
    const el = getEl(key, field);
    if (el) {
      try {
        el.focus();
      } catch {
        // ignore
      }
    }
    insertTokenIntoField(key, field, token);
  };

  const onDragOverAllow = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight={900} gutterBottom>
              Mail Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure when emails are sent and what they say. Saved locally in <code>storage/platform-notifications.json</code>.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Events: {EVENT_DEFS.map((d) => d.key).join(', ')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" onClick={load} disabled={loading || saving}>
              Reload
            </Button>
            <Button variant="outlined" color="warning" onClick={resetAllToDefaults} disabled={loading || saving}>
              Reset all
            </Button>
            <Button variant="contained" onClick={() => saveAll()} disabled={loading || saving} startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}>
              Save
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {updatedAt ? `Last updated: ${updatedAt}` : 'Not saved yet'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {EVENT_DEFS.map((def) => {
            const tpl = items[def.key] || buildDefaults()[def.key];
            const enabled = Boolean(tpl?.enabled);
            const tab = getTab(def.key);
            const expanded = activeKey === def.key;

            return (
              <Accordion
                key={def.key}
                expanded={expanded}
                onChange={(_, isExpanded) => setActiveKey(isExpanded ? def.key : '')}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  boxShadow: expanded ? '0 8px 24px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: expanded ? 'grey.50' : 'background.paper',
                    transition: 'background-color 0.18s ease',
                    '&:hover': {
                      bgcolor: expanded ? 'grey.50' : 'grey.50',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'transform 0.18s ease',
                      color: expanded ? 'primary.main' : 'text.secondary',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(180deg)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <NotificationsIcon fontSize="small" />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Typography fontWeight={900} noWrap>
                            {def.title}
                          </Typography>
                          {def.key === 'product_uploaded' && (
                            <Chip size="small" label="New" color="primary" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {def.description}
                        </Typography>
                      </Box>
                    </Box>

                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      control={<Switch checked={enabled} onChange={(e) => updateItem(def.key, { enabled: e.target.checked })} />}
                      label={enabled ? 'On' : 'Off'}
                      sx={{ ml: 0 }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography fontWeight={900}>Template</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button size="small" variant="outlined" color="warning" onClick={() => resetEventToDefaults(def.key)}>
                            Reset
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                          {def.variables.map((v) => (
                            <Chip
                              key={v}
                              size="small"
                              label={`{{${v}}}`}
                              variant="outlined"
                              onClick={() => insertVar(def.key, v)}
                              draggable
                              onDragStart={(e) => onVarDragStart(e, v)}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                          label="Subject"
                          value={tpl.subject}
                          onChange={(e) => updateItem(def.key, { subject: e.target.value })}
                          inputRef={(el) => setRef(def.key, 'subject', el)}
                          onFocus={() => setLastFocus(def.key, 'subject')}
                          onClick={() => setLastFocus(def.key, 'subject')}
                          onKeyUp={() => setLastFocus(def.key, 'subject')}
                          onMouseUp={() => setLastFocus(def.key, 'subject')}
                          onDragOver={onDragOverAllow}
                          onDrop={(e) => onDropToken(e, def.key, 'subject')}
                          fullWidth
                          sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Tabs value={tab} onChange={(_, v) => setTab(def.key, v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
                        <Tab value="html" label="HTML" />
                        <Tab value="text" label="Text" />
                        <Tab value="preview" label="Preview" />
                      </Tabs>

                      {tab === 'html' && (
                        <TextField
                          fullWidth
                          multiline
                          minRows={10}
                          label="HTML"
                          value={tpl.html}
                          onChange={(e) => updateItem(def.key, { html: e.target.value })}
                          inputRef={(el) => setRef(def.key, 'html', el)}
                          onFocus={() => setLastFocus(def.key, 'html')}
                          onClick={() => setLastFocus(def.key, 'html')}
                          onKeyUp={() => setLastFocus(def.key, 'html')}
                          onMouseUp={() => setLastFocus(def.key, 'html')}
                          onDragOver={onDragOverAllow}
                          onDrop={(e) => onDropToken(e, def.key, 'html')}
                          sx={{ fontFamily: 'monospace' }}
                        />
                      )}

                      {tab === 'text' && (
                        <TextField
                          fullWidth
                          multiline
                          minRows={10}
                          label="Plain text"
                          value={tpl.text}
                          onChange={(e) => updateItem(def.key, { text: e.target.value })}
                          inputRef={(el) => setRef(def.key, 'text', el)}
                          onFocus={() => setLastFocus(def.key, 'text')}
                          onClick={() => setLastFocus(def.key, 'text')}
                          onKeyUp={() => setLastFocus(def.key, 'text')}
                          onMouseUp={() => setLastFocus(def.key, 'text')}
                          onDragOver={onDragOverAllow}
                          onDrop={(e) => onDropToken(e, def.key, 'text')}
                        />
                      )}

                      {tab === 'preview' && (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: 'background.default',
                          }}
                        >
                          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Preview (sandboxed)</Typography>
                            <Chip size="small" label={tpl.subject ? `Subject: ${tpl.subject}` : 'No subject'} />
                          </Box>
                          <Box sx={{ height: 300 }}>
                            <iframe
                              title="Notification preview"
                              sandbox="allow-same-origin"
                              style={{ width: '100%', height: '100%', border: 0, background: 'white' }}
                              srcDoc={tpl.html || '<div style="font-family:Arial,sans-serif;padding:16px;color:#666">No HTML yet</div>'}
                            />
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default PlatformNotificationsTab;
